import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Alert, Box, Typography,
    InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Lock as LockIcon } from '@mui/icons-material';

const LAST_SELLER_KEY = 'capriata_last_validator_id';

// Diálogo para firmar la carga del pedido con usuario + contraseña del vendedor.
// Preselecciona al último vendedor que confirmó en este equipo (mostrador compartido)
// o, si no hay, al usuario logueado; así normalmente solo hay que escribir la contraseña.
const ValidateOrderDialog = ({ open, onClose, onConfirm, users, currentUser, summary }) => {
    const [sellerId, setSellerId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setPassword('');
        setError('');
        setShowPassword(false);
        let lastId = null;
        try { lastId = localStorage.getItem(LAST_SELLER_KEY); } catch (e) { /* sin storage */ }
        const exists = (id) => users.some(u => String(u.id) === String(id));
        if (lastId && exists(lastId)) setSellerId(users.find(u => String(u.id) === String(lastId)).id);
        else if (currentUser && exists(currentUser.id)) setSellerId(currentUser.id);
        else setSellerId('');
    }, [open, users, currentUser]);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!sellerId || !password || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            await onConfirm(sellerId, password);
            try { localStorage.setItem(LAST_SELLER_KEY, String(sellerId)); } catch (err) { /* sin storage */ }
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo validar. Revisá usuario y contraseña.');
            setPassword('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
            <form onSubmit={handleSubmit}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon color="primary" /> Confirmar pedido
                </DialogTitle>
                <DialogContent>
                    {summary && (
                        <Box sx={{ bgcolor: '#f6f7f8', borderRadius: 1, p: 1.5, mb: 2 }}>
                            {summary.clientName && (
                                <Typography variant="body2" fontWeight={600}>{summary.clientName}</Typography>
                            )}
                            <Typography variant="body2" color="textSecondary">
                                {summary.itemsCount} {summary.itemsCount === 1 ? 'producto' : 'productos'} · Total ${summary.total.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Ingresá tu contraseña para registrar quién carga el pedido.
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Vendedor</InputLabel>
                        <Select value={sellerId} label="Vendedor" onChange={(e) => { setSellerId(e.target.value); setError(''); }}>
                            {users.map(u => (
                                <MenuItem key={u.id} value={u.id}>
                                    {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        size="small"
                        autoFocus
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        error={Boolean(error)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} onClick={() => setShowPassword(v => !v)} edge="end">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!sellerId || !password || submitting}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ValidateOrderDialog;
